/**
 * @file CategoryPagination.js
 * @module src/js/components/CategoryPagination
 * @description
 * **A reusable pagination component for navigating through categories.**
 * - Displays a horizontal list of categories.
 * - Highlights the currently selected category.
 * - Allows users to switch categories by clicking on them.
 * - Smoothly scrolls to the header section upon category change.
 * - Optional `rows` lays categories out in that many rows so long lists stay on screen.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2026-10-03: Category bar uses two rows so every category is visible without horizontal scrolling. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useTheme } from "../contexts/ThemeProvider";

export default function CategoryPagination({
    categories,
    currentCategory,
    setCurrentCategory,
    headerRef,
    rows = 1,
}) {
    const { theme } = useTheme();
    const wrapped = rows > 1;
    const columns = Math.max(1, Math.ceil(categories.length / Math.max(rows, 1)));

    return (
        <div className="center">
            <div
                className={wrapped ? 'pagination pagination-wrap' : 'pagination'}
                style={wrapped ? { '--category-columns': columns } : {
                    overflowX: 'auto',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    justifyContent: 'safe center',
                    alignItems: 'center',
                    scrollbarWidth: 'thin',
                    WebkitOverflowScrolling: 'touch'
                }}
            >
                {categories.map((item, i) => (
                    <div
                        key={i}
                        className={item === currentCategory ? 'active' : ''}
                        onClick={() => {
                            setCurrentCategory(item);
                            [100, 300].forEach((delay) => {
                                setTimeout(() => headerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), delay);
                            });
                        }}
                        style={{
                            width: wrapped ? 'auto' : 'fit-content',
                            color: theme === 'dark' ? 'white' : 'black',
                            borderRadius: '5px'
                        }}
                    >
                        {item}
                    </div>
                ))}
            </div>
        </div>
    );
}
